import { useEffect, useState } from 'react'
import { Smile, Star, Users } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { EmptyState, Skeleton } from '@/components/States'
import { RelativeTime } from '@/components/DataDisplay'
import {
  fetchCsat,
  fetchAgentStats,
  type CsatSummary,
  type AgentStatRow,
  TK_STR as S,
} from './ticketsApi'

/** A7.21 — CSAT so'rovnomasi. O'rtacha baho + taqsimot + so'nggi izohlar (greenfield). */
export function CsatScreen() {
  const [data, setData] = useState<CsatSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    fetchCsat(ac.signal)
      .then(setData)
      .finally(() => !ac.signal.aborted && setLoading(false))
    return () => ac.abort()
  }, [])

  const maxCount = data ? Math.max(1, ...data.distribution.map((d) => d.count)) : 1
  const empty = !loading && (!data || data.responses === 0)

  return (
    <>
      <PageHeader title={S.csat} screenId="A7.21" description={S.csatNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-3">
            <div className="flex gap-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-20 w-40" />)}</div>
            <Skeleton className="h-40 w-full" />
          </div>
        ) : empty ? (
          <EmptyState title={S.greenfield} icon={<Smile size={28} strokeWidth={1.5} />} />
        ) : (
          data && (
            <div className="max-w-2xl space-y-3">
              <div className="flex flex-wrap gap-2">
                <Tile label={S.avgScore} value={`${data.avg_score.toFixed(2)} / 5`} icon={<Star size={18} className="text-warning" />} accent />
                <Tile label={S.responses} value={data.responses.toLocaleString()} />
              </div>

              <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                <div className="mb-2 text-sm font-medium text-secondary">{S.distribution}</div>
                <div className="space-y-1.5">
                  {[5, 4, 3, 2, 1].map((score) => {
                    const row = data.distribution.find((d) => d.score === score)
                    const count = row?.count ?? 0
                    return (
                      <div key={score} className="flex items-center gap-3 text-sm">
                        <span className="flex w-10 shrink-0 items-center gap-0.5 text-secondary">{score}<Star size={11} className="text-warning" /></span>
                        <div className="relative h-4 flex-1 overflow-hidden rounded bg-surface-3">
                          <div className="h-full rounded bg-accent/70" style={{ width: `${Math.max(2, (count / maxCount) * 100)}%` }} />
                        </div>
                        <span className="w-12 shrink-0 text-right font-mono text-sm tabular text-primary">{count.toLocaleString()}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {data.recent.length > 0 && (
                <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="mb-2 text-sm font-medium text-secondary">{S.recent}</div>
                  <div className="divide-y divide-border-subtle">
                    {data.recent.map((r) => (
                      <div key={r.id} className="flex items-start gap-3 py-2 text-sm">
                        <span className="flex shrink-0 items-center gap-0.5 text-warning">{r.score}<Star size={11} /></span>
                        <span className="min-w-0 flex-1 text-secondary">{r.comment || '—'}</span>
                        <RelativeTime value={r.created_at} className="shrink-0 text-xs text-tertiary" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </>
  )
}

/** A7.22 — Agent statistikasi. Har agent bo'yicha yuk/samaradorlik (greenfield). */
export function AgentStats() {
  const [rows, setRows] = useState<AgentStatRow[] | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    fetchAgentStats(ac.signal).then(setRows)
    return () => ac.abort()
  }, [])

  return (
    <>
      <PageHeader title={S.agents} screenId="A7.22" description={S.agentsNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {!rows ? (
          <div className="space-y-1.5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title={S.greenfield} icon={<Users size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border-default bg-surface-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-default text-left text-xs text-tertiary">
                  <th className="px-3 py-2 font-medium">{S.agent}</th>
                  <th className="px-3 py-2 text-right font-medium">{S.assigned}</th>
                  <th className="px-3 py-2 text-right font-medium">{S.resolved}</th>
                  <th className="px-3 py-2 text-right font-medium">{S.avgResponse}</th>
                  <th className="px-3 py-2 text-right font-medium">{S.csat}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {rows.map((a) => (
                  <tr key={a.agent_id} className="hover:bg-surface-3">
                    <td className="px-3 py-2 text-primary">{a.name}</td>
                    <td className="px-3 py-2 text-right font-mono tabular text-secondary">{a.assigned}</td>
                    <td className="px-3 py-2 text-right font-mono tabular text-secondary">{a.resolved}</td>
                    <td className="px-3 py-2 text-right font-mono tabular text-secondary">{a.avg_response_min}</td>
                    <td className="px-3 py-2 text-right font-mono tabular text-primary">{a.csat ? a.csat.toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

function Tile({ label, value, icon, accent }: { label: string; value: string; icon?: React.ReactNode; accent?: boolean }) {
  return (
    <div className="inline-flex min-w-36 items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        <div className={`font-mono text-xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>{value}</div>
      </div>
    </div>
  )
}
